import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  BORDER,
  CONTENT_W,
  MARGIN,
  X0,
  buildColumnStyles,
  drawLedgerSignatureFooter,
  drawLedgerStatementHeader,
  formatQtyCell,
  ledgerTableBaseStyles,
  loadLedgerLogos,
  sumNumeric,
} from "./ledgerStatementPdfShared";
import { computeShelfLifePercent } from "./computeShelfLifePercent";

const BATCH_COL_RATIOS = [0.09, 0.19, 0.05, 0.09, 0.08, 0.08, 0.07, 0.08, 0.08, 0.1];

function formatBatchDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" });
}

function splitStockByCondition(quantity, condition) {
  const qty = Number(quantity) || 0;
  const c = String(condition || "").toUpperCase();
  if (
    c.includes("DAMAGED") ||
    c.includes("HOLD") ||
    c.includes("EXPIRED") ||
    c.includes("BAD")
  ) {
    return { goodStock: 0, damagedStock: qty };
  }
  return { goodStock: qty, damagedStock: 0 };
}

function mapBatchReportRow(item) {
  const { goodStock, damagedStock } = splitStockByCondition(item.quantity, item.condition);
  const totalStock = goodStock + damagedStock;
  return {
    skuCode: item.productCode || "",
    productDescription: item.skuName || item.productDescription || "",
    uom: item.uom || "",
    batchNumber: item.batchNumber || "",
    manufacturingDate: item.manufacturingDate,
    expiryDate: item.expiryDate,
    shelfLifePercent: computeShelfLifePercent(item.manufacturingDate, item.expiryDate),
    goodStock,
    damagedStock,
    totalStock,
  };
}

function batchColumnStyles() {
  const styles = buildColumnStyles(BATCH_COL_RATIOS);
  styles[0].halign = "left";
  styles[1].halign = "left";
  styles[2].halign = "center";
  styles[3].halign = "left";
  styles[4].halign = "center";
  styles[5].halign = "center";
  styles[6].halign = "center";
  for (let i = 7; i <= 9; i += 1) {
    styles[i].halign = "right";
  }
  return styles;
}

/**
 * Build A4 landscape INVENTORY BATCH WISE REPORT PDF from API batch rows.
 */
export async function exportInventoryBatchReportPdf(rows, filename, context = {}) {
  const batchRows = (rows || []).map(mapBatchReportRow);

  const totalRow = [
    "Total",
    "",
    "",
    "",
    "",
    "",
    "",
    formatQtyCell(sumNumeric(batchRows.map((r) => r.goodStock))),
    formatQtyCell(sumNumeric(batchRows.map((r) => r.damagedStock))),
    formatQtyCell(sumNumeric(batchRows.map((r) => r.totalStock))),
  ];

  const bodyRows = batchRows.map((row) => [
    row.skuCode,
    row.productDescription,
    row.uom,
    row.batchNumber,
    formatBatchDate(row.manufacturingDate),
    formatBatchDate(row.expiryDate),
    row.shelfLifePercent,
    formatQtyCell(row.goodStock),
    formatQtyCell(row.damagedStock),
    formatQtyCell(row.totalStock),
  ]);

  const logos = await loadLedgerLogos();

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const tableStartY = drawLedgerStatementHeader(doc, context, logos, {
    title: "INVENTORY BATCH WISE REPORT",
    clientHeading: "Client Name and Address",
  });

  autoTable(doc, {
    startY: tableStartY,
    tableWidth: CONTENT_W,
    margin: { left: X0, right: X0, bottom: MARGIN + 84 },
    head: [
      [
        { content: "SKU Code", styles: { valign: "middle" } },
        { content: "Product\nDescription", styles: { valign: "middle" } },
        { content: "UOM", styles: { valign: "middle" } },
        { content: "Batch\nNo.", styles: { valign: "middle" } },
        { content: "Mfg.\nDate", styles: { valign: "middle" } },
        { content: "Expiry\nDate", styles: { valign: "middle" } },
        { content: "Shelf Life %", styles: { valign: "middle", halign: "center" } },
        { content: "Good\nStock", styles: { valign: "middle" } },
        { content: "Damaged\nStock", styles: { valign: "middle" } },
        { content: "Total\nStock", styles: { valign: "middle" } },
      ],
    ],
    body: [totalRow, ...bodyRows],
    ...ledgerTableBaseStyles,
    columnStyles: batchColumnStyles(),
    showHead: "everyPage",
    rowPageBreak: "avoid",
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === 0) {
        data.cell.styles.fontStyle = "bold";
        if (data.column.index >= 7) {
          data.cell.styles.halign = "right";
        }
      }
    },
  });

  drawLedgerSignatureFooter(doc, context, doc.lastAutoTable.finalY);

  doc.save(filename);
}

export async function fetchAllInventoryBatchReportRows(reportService, params) {
  const limit = 100;
  let page = 1;
  let all = [];
  let total = Infinity;
  let reportStartDate = null;
  let reportEndDate = null;

  while (all.length < total) {
    const res = await reportService.getInventoryReportByBatch({ ...params, page, limit });
    const chunk = Array.isArray(res?.data) ? res.data : [];
    if (page === 1) {
      total = Number(res?.count ?? chunk.length);
      reportStartDate = res?.reportStartDate ?? null;
      reportEndDate = res?.reportEndDate ?? null;
    }
    all = all.concat(chunk);
    if (chunk.length < limit) break;
    page += 1;
  }

  return { rows: all, reportStartDate, reportEndDate };
}
